import React, { useEffect, useState } from "react";
import "pages/transaction/filterTransaction/FilterTransaction.scss";
import {
  Button,
  DatePicker,
  MultiSelectInput,
  TextInput,
} from "@pickit/pickit-components";
import { Field, Form } from "react-final-form";
import {
  FILTER_TRANSACTION_OPTIONS,
  VALIDATION_REGEX,
} from "utils/constants";
import useValidationSchema from "hooks/useValidationSchema";
import { FilterTransactionPropsType, FilterValuesType } from "./types";
import "./i18n";
import { useTranslation } from "react-i18next";
import FilterPanel from "component/filterPanel/FilterPanel";
import { FILTER_PANEL_FORM_CLASS } from "component/filterPanel/types";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { parseQueryParams } from "utils/utils";
import { URLTransactionContainerType } from "../types";

export const FilterTransaction: React.FC<FilterTransactionPropsType> = ({
  onSubmit,
  filters,
  origins,
  validationSchema,
  regions,
  workzones,
  fetchingWorkzones,
  fetchingOrigins,
  testId,
  setCollapseTable,
  onUpdateWorkzones,
  onUpdateSla,
  setActualRegion,
  setActualWorkzone,
  getDateRange,
}): JSX.Element => {
  const { t } = useTranslation();

  const [maxDate, setMaxDate] = useState("");

  const [filteredOrigins, setFilteredOrigins] = useState<{ id: string; name: string }[]>(origins || []);

  const onUpdateOrigins = (regionsStr?: string, workzonesStr?: string) => {
    const regionsSelected = regionsStr ? regionsStr?.split(",") : [];
    const workzonesSelected = workzonesStr ? workzonesStr?.split(",") : [];

    if (regionsSelected.length === 1 && regionsSelected[0] !== filters?.actualRegion) {
      setActualRegion(regionsSelected[0]);
      setActualWorkzone("");
      workzonesSelected.length = 0;
    }
  
    if (workzonesSelected.length === 1 && workzonesSelected[0] !== filters?.actualWorkzone) {
      setActualWorkzone(workzonesSelected[0]);
    }
  
    let newFilteredOrigins = origins.filter(o => 
      regionsStr && workzonesStr
        ? regionsSelected.includes(o.region) && workzonesSelected.includes(o.workZone)
        : regionsStr
          ? regionsSelected.includes(o.region)
          : origins
    );
  
    setFilteredOrigins(newFilteredOrigins);
  };
  
  const params = useLocation();
  const test: URLTransactionContainerType = parseQueryParams(
    params.search
  );
  if (test.sku && typeof test.sku === 'string') {
    test.sku = decodeURIComponent(test.sku)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .join(',');
  }
  const paramRegions = test.regions;
  const paramWorkZones = test.workZones;

  useEffect(() => {
    if (origins && !fetchingOrigins) {
      onUpdateOrigins(filters?.actualRegion, filters?.actualWorkzone);
    } else if (paramRegions) {
      onUpdateOrigins(paramRegions, paramWorkZones || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origins, fetchingOrigins, params]);

  return (
    <><FilterPanel
      layout="transactions"
      title={t("global:filter")}
      testId={testId}
      collapseTable={setCollapseTable}
    >
      <div className="container-transaction-fluid">
        <div className="container-transaction-row">
          <div className="container-transaction-col-auto"></div>
          <div className="container-transaction-col">
            <div className="container-transaction-row">
              <Form
                onSubmit={(values: FilterValuesType) => {
                  const cleanedSku = values.sku
                    ? values.sku
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .join(',')
                    : undefined;
                  return onSubmit({
                    ...values,
                    sku: cleanedSku,
                    pickerId: values.pickerId
                      ? Number(values.pickerId)
                      : undefined,
                    date: {
                      from: values.date?.from,
                      until: values.date?.until || values.date?.from
                    }
                  });
                }}
                keepDirtyOnReinitialize
                initialValues={filters}
                validate={useValidationSchema(validationSchema)}
                mutators={{
                  resetWorkzones: ([_, value], state, { changeValue }) => {
                    const regions = value?.split(",") || [];
                    if (!value || regions?.length !== 1) {
                      changeValue(state, "workZones", () => "");
                      changeValue(state, "origins", () => "");
                    }
                  },
                  resetOrigins: ([field, value], state, { changeValue }) => {
                    const workzones = field?.split(",") || [];
                    if (!field || workzones?.length !== 1) {
                      changeValue(state, "origins", () => "");
                    }
                  },
                }}
              >
                {({ handleSubmit, form, values, invalid }) => (
                  <form
                    className={FILTER_PANEL_FORM_CLASS}
                    onSubmit={handleSubmit}
                  >
                    <div className="container-transaction-col-sm-1">
                      <Field
                        type="text"
                        name="transactionCode"
                        label={t("filterTransactions:transactionCodeLabel")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        placeholder={t(
                          "filterTransactions:transactionCodePlaceholder"
                        )}
                        maxLength={6}
                        pattern={VALIDATION_REGEX.lettersAndNumbers}
                        testId={`${testId}-code`}
                      />
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        type="text"
                        name="orderNumber"
                        label={t("filterTransactions:orderNumberLabel")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        placeholder={t(
                          "filterTransactions:orderNumberPlaceholder"
                        )}
                        maxLength={50}
                        pattern={VALIDATION_REGEX.lettersAndNumbers}
                        testId={`${testId}-order-number`} />
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        type="text"
                        name="pickerId"
                        label={t("filterTransactions:pickerIdLabel")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        pattern={VALIDATION_REGEX.regNumber}
                        allowDecimal={false}
                        placeholder={t(
                          "filterTransactions:pickerIdPlaceholder"
                        )}
                        testId={`${testId}-picker-id`}
                        maxLength={15}
                      />
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        type="text"
                        name="tripId"
                        maxLength={8}
                        label={t("filterTransactions:tripCodeLabel")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        placeholder={t(
                          "filterTransactions:tripCodePlaceholder"
                        )}
                        pattern={VALIDATION_REGEX.lettersAndNumbers}
                        testId={`${testId}-trip-id`} />
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        name="state"
                        placeholder={t("filterTransactions:statePlaceholder")}
                        options={FILTER_TRANSACTION_OPTIONS.map((o) => ({
                          ...o,
                          label: t(o.label),
                        }))}
                        testId={`${testId}-state`}
                        label={t("filterTransactions:stateLabel")}
                        showProperty="label"
                      >
                        {(props: any) => <MultiSelectInput {...props} />}
                      </Field>
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        name="regions"
                        label={t("filterTransactions:regionsLabel")}
                        placeholder={t("filterTransactions:regionsPlaceholder")}
                        options={regions?.map((r) => ({ ...r, label: r.name }))}
                        testId={`${testId}-regions`}
                        showProperty="label"
                        onChange={(e: string) => {
                          form.mutators.resetWorkzones();
                          onUpdateWorkzones(e);
                          onUpdateOrigins(e, values?.workZones);
                        }}
                      >
                        {(props: any) => (
                          <MultiSelectInput
                            {...props}
                            labelAll={t("global:allF")}
                          />
                        )}
                      </Field>
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        name="workZones"
                        label={t("filterTransactions:workzonesLabel")}
                        placeholder={t(
                          "filterTransactions:workzonesPlaceholder"
                        )}
                        disabled={(values.regions?.split(",") || []).length > 1 ||
                          !values.regions}
                        options={workzones}
                        testId={`${testId}-workzones`}
                        onChange={(e: string) => {
                          form.mutators.resetOrigins();
                          onUpdateOrigins(values?.regions, e);
                        }}
                      >
                        {(props: any) => (
                          <MultiSelectInput
                            {...props}
                            labelAll={t("global:allF")}
                            isLoading={fetchingWorkzones}

                          />
                        )}
                      </Field>
                    </div>

                    <div className="container-transaction-col-sm-1">
                      <Field
                        name="origins"
                        label={t("filterTransactions:originsLabel")}
                        placeholder={t("filterTransactions:originsPlaceholder")}
                        options={filteredOrigins}
                        testId={`${testId}-origins`}
                        disabled={!values.regions || ((values.regions?.split(",") || []).length > 1) ||
                          ((values.workZones?.split(",") || []).length > 1)}
                      >
                        {(props: any) => (
                          <MultiSelectInput
                            {...props}
                            labelAll={t("global:allM")}
                            isLoading={fetchingOrigins}
                          />
                        )}
                      </Field>
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <div
                        className="datePicker-filter-transaction"
                        id="datePicker-filter-transaction"
                      >
                        <Field
                          type="text"
                          className="Admin-Pickers-input-select"
                          name="date"
                          placeholder={t("filterTransactions:datePlaceholder")}
                          label={t("filterTransactions:dateLabel")}
                          language="es"
                          outputFormat="DD/MM/YY"
                          testId={`${testId}-sla-expiration`}
                        >
                          {(props: any) => {
                            return (
                              <DatePicker
                                {...props}
                                maxDate={moment(maxDate, 'DD/MM/YYYY').format('YYYY-MM-DD')}
                                onChange={(value: any) => {
                                  form.mutators.resetOrigins();
                                  setMaxDate(value?.from && getDateRange(value.from))
                                  onUpdateSla(value?.from, value?.until);
                                }}
                              />
                            );
                          }}
                        </Field>
                      </div>
                    </div>
                    <div className="container-transaction-col-sm-1">
                      <Field
                        name="sku"
                        label={t("filterTransactions:skuLabel")}
                        placeholder={t("filterTransactions:skuPlaceholder")}
                        testId={`${testId}-sku`}
                        type="text"
                        component={TextInput}
                        className="Admin-Pickers-input"
                        maxLength={50}
                      />
                    </div>
                    <div className="container-transaction-col-sm-1" />
                    <div className="container-transaction-col-sm-1 transaction-filter-action-wrapper">
                      <div className="checkbox-filter-transaction">
                      </div>

                      <div className="end">
                        <Button
                          hierarchy="secondary"
                          label={t("global:search")}
                          iconName="search"
                          size="large"
                          type="submit"
                          testId={`${testId}-submit-button`}
                          disabled={invalid}
                        />
                      </div>
                    </div>
                  </form>
                )}
              </Form>
            </div>
          </div>
        </div>
      </div>
    </FilterPanel>
    </>
  );
};
